import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Session } from '../../types/api';
import { formatRelative, formatTime } from '../../lib/format';

const HOURS_BEFORE = 1;
const HOURS_AFTER = 7;

/**
 * The hero of the console: the next few hours, as a strip.
 *
 * Everything else here is a count. This is the only view that answers the
 * question an operator actually opens the dashboard with — *what is
 * happening right now, and what is about to* — so it gets the top of the
 * page and the live treatment. The "now" line moves on its own.
 */
export function DayTimeline({ sessions }: { sessions: Session[] }) {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, []);

  const windowStart = now - HOURS_BEFORE * 3600_000;
  const windowEnd = now + HOURS_AFTER * 3600_000;
  const span = windowEnd - windowStart;

  const visible = sessions
    .filter((s) => {
      const start = new Date(s.startTime).getTime();
      const end = new Date(s.endTime).getTime();
      return end > windowStart && start < windowEnd;
    })
    .sort((a, b) => +new Date(a.startTime) - +new Date(b.startTime));

  // Lay sessions onto as few rows as possible without overlapping.
  const rows: Session[][] = [];
  for (const session of visible) {
    const start = new Date(session.startTime).getTime();
    const row = rows.find((r) => {
      const last = r[r.length - 1];
      return new Date(last.endTime).getTime() <= start;
    });
    if (row) row.push(session);
    else rows.push([session]);
  }

  // When the window is empty the panel still has a job: say when the next
  // lesson actually is.
  const next = sessions
    .filter(
      (s) =>
        new Date(s.startTime).getTime() > windowEnd &&
        (s.status === 'scheduled' || s.status === 'in_progress'),
    )
    .sort((a, b) => +new Date(a.startTime) - +new Date(b.startTime))[0];

  const hourMarks: number[] = [];
  const firstMark = new Date(windowStart);
  firstMark.setMinutes(0, 0, 0);
  for (let t = firstMark.getTime(); t <= windowEnd; t += 3600_000) {
    if (t >= windowStart) hourMarks.push(t);
  }

  const pct = (t: number) => ((t - windowStart) / span) * 100;

  return (
    <div className="panel overflow-hidden">
      <div className="flex items-center justify-between border-b border-rule px-4 py-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">The next few hours</h2>
          <p className="mt-0.5 text-xs text-ink-3">
            Lessons from an hour ago to {HOURS_AFTER} hours ahead, in your local time.
          </p>
        </div>
        <span className="flex items-center gap-1.5 text-xs text-ink-3">
          <span className="pulse-dot" aria-hidden="true" />
          now {formatTime(new Date(now).toISOString())}
        </span>
      </div>

      <div className="relative px-4 py-4">
        {/* Hour grid — recessive, behind the marks. */}
        <div className="pointer-events-none absolute inset-x-4 inset-y-4">
          {hourMarks.map((t) => (
            <div
              key={t}
              className="absolute inset-y-0 border-l border-rule"
              style={{ left: `${pct(t)}%` }}
            />
          ))}
        </div>

        <div className="relative mb-2 h-4">
          {hourMarks.map((t) => (
            <span
              key={t}
              className="tabular absolute -translate-x-1/2 text-[0.6875rem] text-ink-3"
              style={{ left: `${pct(t)}%` }}
            >
              {new Date(t).toLocaleTimeString(undefined, { hour: 'numeric' })}
            </span>
          ))}
        </div>

        {rows.length === 0 ? (
          <div className="relative py-6 text-center">
            <p className="text-xs text-ink-3">Nothing booked in the next {HOURS_AFTER} hours.</p>
            {next && (
              <p className="mt-1 text-xs text-ink-2">
                The next lesson is{' '}
                <Link
                  to={`/sessions/${next.id}`}
                  className="text-accent hover:underline"
                >
                  {next.student.fullName} with {next.tutor.fullName}
                </Link>
                , {formatRelative(next.startTime)}.
              </p>
            )}
          </div>
        ) : (
          <div className="relative space-y-1.5">
            {rows.map((row, i) => (
              // Lanes are recomputed from the sessions on every render and
              // have no identity of their own; the row index is what they are.
              // biome-ignore lint/suspicious/noArrayIndexKey: explained above
              <div key={i} className="relative h-9">
                {row.map((session) => {
                  const start = new Date(session.startTime).getTime();
                  const end = new Date(session.endTime).getTime();
                  const left = Math.max(pct(start), 0);
                  const right = Math.min(pct(end), 100);
                  const live = session.status === 'in_progress';
                  return (
                    <Link
                      key={session.id}
                      to={`/sessions/${session.id}`}
                      style={{ left: `${left}%`, width: `${Math.max(right - left, 2)}%` }}
                      title={`${session.student.fullName} with ${session.tutor.fullName} · ${formatTime(session.startTime)}–${formatTime(session.endTime)}`}
                      className={`absolute inset-y-0 flex items-center overflow-hidden rounded border px-2 text-[0.6875rem] transition-colors ${
                        live
                          ? 'border-live/40 bg-live-wash text-live-text hover:border-live'
                          : 'border-accent/25 bg-accent-wash text-accent hover:border-accent/60'
                      }`}
                    >
                      {live && <span className="pulse-dot mr-1.5 shrink-0" aria-hidden="true" />}
                      <span className="truncate font-medium">
                        {session.student.fullName}
                      </span>
                      <span className="ml-1 truncate opacity-70">
                        · {session.tutor.fullName}
                      </span>
                    </Link>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* The now line sits above everything — it is the reference. It
            shares the grid's inset frame so both measure from the same
            origin. */}
        <div className="pointer-events-none absolute inset-x-4 inset-y-2 z-10">
          <div
            className="absolute inset-y-0 w-0.5 -translate-x-1/2 rounded bg-live"
            style={{ left: `${pct(now)}%` }}
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}
